import { API, deleteRequest, getRequest, postRequest, putRequest } from '@/api/api';
import { generateSearchParams } from '@/utils/generate-search-params';
import type { List } from '@/utils/list';

import {
  mapInviteCode,
  mapInviteToDto,
  mapJoinedVessel,
  mapSeatAvailability,
} from './invites.mappers';
import type {
  CreateInvitation,
  GenerateInviteLinkRequest,
  InviteCodeDto,
  InviteCodeRole,
  InviteLink,
  JoinInviteCodeDto,
  SeatAvailabilityDto,
  SingleInvite,
} from './invites.types';

export const invite = async (invite: CreateInvitation) => {
  await postRequest(API.PEOPLE, '/invites', mapInviteToDto(invite));
};

export const generateInviteLink = async (body: GenerateInviteLinkRequest) => {
  const { data } = await postRequest<InviteLink, GenerateInviteLinkRequest>(
    API.PEOPLE,
    '/invites/link',
    body,
  );
  return data;
};

/**
 * Mints a one-time code for `role` on the vessel. Scoped by an explicit `X-Service-Id`: the vessel
 * page can show a vessel other than the active account.
 */
export const createInviteCode = async (vesselId: string, role: InviteCodeRole) => {
  const { data } = await postRequest<InviteCodeDto, { role: InviteCodeRole }>(
    API.PEOPLE,
    '/invites/code',
    { role },
    false,
    vesselId,
  );
  return mapInviteCode(data);
};

/**
 * The vessel's pending codes, newest first, for its owner group or captain — on any plan, unlike
 * `GET /invites/vessel`, which needs crew management.
 */
export const getInviteCodes = async (vesselId: string) => {
  const { data } = await getRequest<InviteCodeDto[]>(
    API.PEOPLE,
    '/invites/code',
    false,
    undefined,
    vesselId,
  );
  return (data ?? []).map(mapInviteCode);
};

/** Puts the caller on the vessel the code was minted for, with its role, and spends the code. */
export const joinByInviteCode = async (code: string) => {
  const { data } = await postRequest<JoinInviteCodeDto, { code: string }>(
    API.PEOPLE,
    '/invites/code/join',
    { code },
  );
  return mapJoinedVessel(data);
};

export const getInviteById = async (id: string) => {
  try {
    const { data } = await getRequest<SingleInvite>(API.PEOPLE, `invites/${id}`);
    return data;
  } catch {
    return null;
  }
};

export const acceptInvitation = async (id: string) => {
  await putRequest(API.PEOPLE, `invites/${id}`, {});
};

export const joinByInviteLink = async (previewId: string) => {
  await postRequest(API.PEOPLE, `/invites/link/${encodeURIComponent(previewId)}/join`, {});
};

export const getInvites = async (params: {
  page: number;
  limit: number;
  person_id?: string;
  service_id?: string;
  event_id?: string;
}) => {
  const searchParams = generateSearchParams({ ...params, service_type: 'vessel' });
  const { data } = await getRequest<List<SingleInvite>>(API.PEOPLE, `invites?${searchParams}`);
  return data;
};

export const deleteInvite = async (id: string, reason?: string): Promise<void> => {
  await deleteRequest(API.PEOPLE, `invites/${id}`, reason ? { reason } : undefined);
};

export const getSeatAvailability = async (serviceId: string, role: string) => {
  const searchParams = generateSearchParams({ service_id: serviceId, role });
  const { data } = await getRequest<SeatAvailabilityDto>(
    API.PEOPLE,
    `invites/seat-availability?${searchParams}`,
  );
  return mapSeatAvailability(data);
};
